import { useTranslation } from 'react-i18next';

import type { Schemas } from '../api/client.js';
import { formatDate, intlLocale } from '../i18n.js';
import { formatMoney } from './site.js';

type Status = Schemas['Invoice']['status'];

const STATUS_STYLE: Record<Status, string> = {
  open: 'bg-amber-100 text-amber-950',
  paid: 'bg-green-100 text-green-900',
  void: 'bg-slate-200 text-slate-800',
  refunded: 'bg-violet-100 text-violet-900',
};

export function InvoiceStatus({ status, overdue = false }: { status: Status; overdue?: boolean }) {
  const { t } = useTranslation();
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-0.5 text-sm font-semibold whitespace-nowrap ${overdue ? 'bg-red-100 text-red-900' : STATUS_STYLE[status]}`}
    >
      {overdue ? t('invoice.overdue') : t(`invoice.status.${status}`)}
    </span>
  );
}

export function vatLabel(rateBp: number) {
  return `${(rateBp / 100).toLocaleString('de-DE')} %`;
}

/** The invoice as a document: prints on its own (T-48); demo invoices say so in large type. */
export function InvoiceDocument({
  invoice,
  timeZone,
}: {
  invoice: Schemas['InvoiceDocument'];
  timeZone?: string | undefined;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const date = (iso: string) => formatDate(iso, lang, timeZone);
  const money = (cents: number) => formatMoney(cents, lang);
  const b = invoice.buyer;
  const country = (code: string) =>
    new Intl.DisplayNames([intlLocale(lang)], { type: 'region' }).of(code) ?? code;
  return (
    <article className="relative flex flex-col gap-6 overflow-hidden rounded-lg border border-slate-200 bg-white p-5 sm:p-8 print:border-0 print:p-0">
      {invoice.demo ? (
        <p className="rounded-md border-2 border-dashed border-amber-500 bg-amber-50 px-4 py-2 text-center font-bold tracking-wide text-amber-950 uppercase">
          {t('invoice.demoStamp')}
        </p>
      ) : null}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="text-sm leading-6">
          {invoice.issuer.map((line, i) => (
            <p key={i} className={i === 0 ? 'text-lg font-bold' : ''}>
              {line}
            </p>
          ))}
        </div>
        <div className="text-right">
          <h1 className="text-2xl font-bold">{t('invoice.title')}</h1>
          <p className="font-mono text-lg">{invoice.number}</p>
          <div className="mt-2">
            <InvoiceStatus status={invoice.status} />
          </div>
        </div>
      </header>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="text-sm leading-6">
          <p className="text-xs font-semibold tracking-wide text-slate-600 uppercase">
            {t('invoice.to')}
          </p>
          <p className="font-semibold">{b.name}</p>
          {b.street ? <p>{b.street}</p> : null}
          {b.postalCode || b.city ? (
            <p>
              {b.postalCode} {b.city}
            </p>
          ) : null}
          <p>{country(b.country)}</p>
          {b.vatId ? (
            <p>
              {t('invoice.vatId')}: {b.vatId}
            </p>
          ) : null}
        </div>
        <dl className="grid grid-cols-[auto_1fr] content-start gap-x-4 gap-y-1 text-sm sm:justify-self-end">
          <dt className="text-slate-700">{t('invoice.issued')}</dt>
          <dd className="font-semibold">{date(invoice.issuedAt)}</dd>
          <dt className="text-slate-700">{t('invoice.due')}</dt>
          <dd className="font-semibold">{date(invoice.dueAt)}</dd>
          {invoice.paidAt ? (
            <>
              <dt className="text-slate-700">{t('invoice.paidOn')}</dt>
              <dd className="font-semibold">{date(invoice.paidAt)}</dd>
            </>
          ) : null}
        </dl>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[28rem] text-left text-sm">
          <thead>
            <tr className="border-b-2 border-slate-800">
              <th scope="col" className="py-2 pr-4">
                {t('invoice.item')}
              </th>
              <th scope="col" className="py-2 text-right">
                {t('invoice.amount')}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-slate-200 align-top">
              <td className="py-3 pr-4">
                <p className="font-semibold">{invoice.description}</p>
                <p className="text-slate-700">
                  {invoice.periodStart && invoice.periodEnd
                    ? t('invoice.period', {
                        from: date(invoice.periodStart),
                        to: date(invoice.periodEnd),
                      })
                    : invoice.interval
                      ? t('invoice.periodFromPayment', {
                          interval: t(`billing.intervals.${invoice.interval}`),
                        })
                      : null}
                </p>
              </td>
              <td className="py-3 text-right tabular-nums">{money(invoice.netCents)}</td>
            </tr>
          </tbody>
          <tfoot className="tabular-nums">
            <tr>
              <th scope="row" className="pt-3 pr-4 text-right font-normal">
                {t('invoice.net')}
              </th>
              <td className="pt-3 text-right">{money(invoice.netCents)}</td>
            </tr>
            <tr>
              <th scope="row" className="pr-4 text-right font-normal">
                {t('invoice.vat', { rate: vatLabel(invoice.vatRateBp) })}
              </th>
              <td className="text-right">{money(invoice.vatCents)}</td>
            </tr>
            <tr className="text-lg">
              <th scope="row" className="pt-1 pr-4 text-right">
                {t('invoice.gross')}
              </th>
              <td className="pt-1 text-right font-bold">{money(invoice.grossCents)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {invoice.vatNote ? (
        <p className="text-sm text-slate-800">{t(`invoice.vatNote.${invoice.vatNote}`)}</p>
      ) : null}

      {invoice.status === 'open' ? (
        <section className="rounded-md bg-slate-50 p-4 text-sm leading-6 print:bg-transparent print:p-0">
          <h2 className="mb-1 font-semibold">{t('invoice.howToPay')}</h2>
          <p>
            {t('invoice.payText', {
              amount: money(invoice.grossCents),
              date: date(invoice.dueAt),
              number: invoice.number,
            })}
          </p>
          {invoice.bank.length > 0 ? (
            <div className="mt-2 font-mono">
              {invoice.bank.map((line, i) => (
                <p key={i}>{line}</p>
              ))}
            </div>
          ) : (
            <p className="mt-2 font-semibold">{t('invoice.noBank')}</p>
          )}
        </section>
      ) : null}
    </article>
  );
}
